import React, { useEffect, useState, useLayoutEffect, useMemo } from "react";
import "../styles/home.css";
import "../styles/table.css";
import "../styles/theme.css";
import Layout from "./Layout";
import DropdownComponent from "./display/DropDownComponent";
import { getShiftReport, getSiteNames } from "../functions/api/details";
import SmallTableComponent from "./display/SmallTable";
import { ShiftReportRow, TotalShiftReportRow } from "../interfaces/interfaces";
import DescriptionList from "./display/DetailsComponent";

const getYesterday = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 2);
  return date.toISOString().slice(0, 10);
};

const getToday = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

const ShiftReportComponent: React.FC = () => {
  const [shiftReportData, setShiftReportData] = useState<any[]>([]);
  const [selectedCurrentDate, setSelectedCurrentDate] = useState<string>(getToday);
  const [code, setSelectedCode] = useState<string>("Lusaka");
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [selectedFromDate, setSelectedFromDate] = useState<string>(getYesterday());
  const [selectedToDate, setSelectedToDate] = useState<string>(getToday());
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch site names on mount
  useEffect(() => {
    const populateSiteItems = async () => {
      const data = await getSiteNames();
      setSiteOptions(Array.isArray(data) ? data : []);
    };
    populateSiteItems();
  }, []);

  // Load shift report whenever code or dates change
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const dipsData = await getShiftReport(code, selectedFromDate, selectedToDate);
        setShiftReportData(Array.isArray(dipsData) ? dipsData : []);
      } catch (err) {
        console.error("Error loading shift report:", err);
        setShiftReportData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [code, selectedFromDate, selectedToDate]);

  // Set loading to false after data is rendered
  useLayoutEffect(() => {
    if (shiftReportData) {
      setLoading(false);
    }
  }, [shiftReportData]);

  // Handlers
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFromDate(e.target.value);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedToDate(e.target.value);
  };

  const handleDropdownChange = (newCode: string) => {
    setSelectedCode(newCode);
  };

  function groupAndProcessRows(data: ShiftReportRow[]): ShiftReportRow[] {
  const tankGroups = new Map<string, ShiftReportRow[]>();
  data.forEach((row) => {
    if (!tankGroups.has(row.tank_name)) {
      tankGroups.set(row.tank_name, []);
    }
    tankGroups.get(row.tank_name)!.push(row);
  });

  const processedRows: ShiftReportRow[] = [];

  tankGroups.forEach((rows) => {
    const sorted = [...rows].sort(
      (a, b) => new Date(a.dip_date).getTime() - new Date(b.dip_date).getTime()
    );

    for (let i = 1; i < sorted.length - 1; i++) {
      const curr = sorted[i];
      const next = sorted[i + 1];

      const volumeDelta = curr.closing_volume - curr.opening_volume;
      const received = volumeDelta > 10 ? curr.received_gross : 0;

      processedRows.push({
        ...curr,
        received_gross: received,
        received_GST: volumeDelta > 10 ? curr.received_GST : 0,
        calculated_dispense: volumeDelta > 10
          ? Math.abs(curr.closing_volume - (curr.opening_volume + curr.received_GST))
          : Math.abs(volumeDelta),
        day_variance: curr.closing_volume - (curr.opening_volume + volumeDelta),
        night_variance: (next.opening_volume || 0) - curr.closing_volume,
      });
    }
  });

  return processedRows;
}
function summarizeVariance(rows: ShiftReportRow[]) {
  const totals = rows.reduce((acc, row) => {
    if (!acc[row.tank_name]) {
      acc[row.tank_name] = {
        tank_name: row.tank_name,
        total_day_variance: 0,
        total_night_variance: 0,
      };
    }
    acc[row.tank_name].total_day_variance += row.day_variance || 0;
    acc[row.tank_name].total_night_variance += row.night_variance || 0;
    return acc;
  }, {} as Record<string, { tank_name: string; total_day_variance: number; total_night_variance: number }>);

  return Object.values(totals);
}

const processedRows = useMemo(() => groupAndProcessRows(shiftReportData), [shiftReportData]);
const varianceSummaryRows = useMemo(() => summarizeVariance(processedRows), [processedRows]);

  return (
    <Layout>
      <div className="home-container" style={{ overflowX: "auto" }}>
        <h2 className="home-header">Shift Report</h2>
        <DescriptionList text="
                        This report shows tank volumes per operating shifts for the day, as well as shows a basic gain/loss overview per tank.
                        /nYou can choose a date range to view for the shifts and for the gain/loss analysis.
                        /n* Note the Received gross value is assigned to a tank that is determined to have increased in volume for the day. This will not work if multiple tanks receive for the day.
                        /nThe Calculated dispense per tank is calculated as: Closing volume - Opening volume.
                        /n* Note if a tank was determined to have received product, the Calculated dispense is then calculated as Closing Volume - (Opening Volume + Received Volume).
                        /nDay varaince is then calculated as: Closing volume - (Book Inventory) => Closing volume - (Opening volume - (Opening volume + Received gross - Calculated dispense))
                        /nNight variance is calculated as the next day's Opening volume - current Closing volume.
                        " 
        />

        <div className="dropdown">
          <DropdownComponent
            options={siteOptions}
            value={code}
            onChange={handleDropdownChange}
            label="Choose code"
          />
          {code && <p className="small">You selected: {code}</p>}
        </div>

        {/* From Date */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ marginRight: "0.5rem" }}>From:</label>
          <div className="date-picker-wrapper">
            <button
              className="date-arrow"
              onClick={() => {
                const prev = new Date(selectedFromDate);
                prev.setDate(prev.getDate() - 1);
                setSelectedFromDate(prev.toISOString().slice(0, 10));
              }}
            >
              ←
            </button>
            <input
              type="date"
              value={selectedFromDate}
              onChange={handleFromDateChange}
              style={{ padding: "0.4rem" }}
            />
            <button
              className="date-arrow"
              onClick={() => {
                const next = new Date(selectedFromDate);
                next.setDate(next.getDate() + 1);
                setSelectedFromDate(next.toISOString().slice(0, 10));
              }}
            >
              →
            </button>
          </div>
        </div>

        {/* To Date */}
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ marginRight: "0.5rem" }}>To:</label>
          <div className="date-picker-wrapper">
            <button
              className="date-arrow"
              onClick={() => {
                const prev = new Date(selectedToDate);
                prev.setDate(prev.getDate() - 1);
                setSelectedToDate(prev.toISOString().slice(0, 10));
              }}
            >
              ←
            </button>
            <input
              type="date"
              value={selectedToDate}
              onChange={handleToDateChange}
              style={{ padding: "0.4rem" }}
            />
            <button
              className="date-arrow"
              onClick={() => {
                const next = new Date(selectedToDate);
                next.setDate(next.getDate() + 1);
                setSelectedToDate(next.toISOString().slice(0, 10));
              }}
            >
              →
            </button>
          </div>
        </div>

        {loading ? (
  <p>Loading...</p>
) : (
  <>
    <h3 className="normal-heading">Shift Report Summary</h3>
    {shiftReportData.length > 0 ? (
      <>
        {(() => {

          const uniqueDates = Array.from(new Set(processedRows.map((r) => r.dip_date)));

          return (
            <>
              {uniqueDates.map((date) => {
                const rowsForDate = processedRows.filter((r) => r.dip_date === date);

                const totalRow: TotalShiftReportRow = {
                  dip_date: date,
                  tank_name: "Total",
                  label: "Summary",
                  opening_volume: rowsForDate.reduce((sum, r) => sum + (r.opening_volume || 0), 0),
                  opening_GST: rowsForDate.reduce((sum, r) => sum + (r.opening_GST || 0), 0),
                  closing_volume: rowsForDate.reduce((sum, r) => sum + (r.closing_volume || 0), 0),
                  closing_GST: rowsForDate.reduce((sum, r) => sum + (r.closing_GST || 0), 0),
                  calculated_dispense: rowsForDate.reduce((sum, r) => sum + (r.calculated_dispense || 0), 0),
                  day_variance: rowsForDate.reduce((sum, r) => sum + (r.day_variance || 0), 0),
                  night_variance: rowsForDate.reduce((sum, r) => sum + (r.night_variance || 0), 0),
                  received_gross: rowsForDate.reduce((sum, r) => sum + (r.received_gross || 0), 0),
                  received_GST: rowsForDate.reduce((sum, r) => sum + (r.received_GST || 0), 0),
                };

                const tableData = [...rowsForDate, totalRow].map(({ dip_date, ...rest }) => rest);

                return (
                  <div key={date} style={{ marginBottom: "2rem" }}>
                    <h4 className="normal-heading">Date: {date}</h4>
                    <div className="table-scroll-wrapper">
                      <div className="table-scroll-inner">
                        <SmallTableComponent
                          data={tableData}
                          showSearchControls={false}
                          rowClassName={(row) => (row.tank_name === "Total" ? "summary-row" : "")}
                          thresholds={{
                          opening_dip_time: { maxTime: "06:00" },   // Below 06:00 → red
                          closing_dip_time: { minTime: "23:00" },
                          opening_level: { min: 250 },
                          closing_level: { min: 250 },
                          }}
                          middleColumnKey={[
                            "closing_dip_time",
                            "calculated_dispense",
                            "received_gross",
                            "day_variance",
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              <div key="variance-summary" style={{ marginTop: "3rem" }}>
                <h4 className="normal-heading">Total Morning & Evening Variance per Tank</h4>
                <div className="table-scroll-wrapper">
                  <div className="table-scroll-inner">
                    <SmallTableComponent
                      data={varianceSummaryRows}
                      showSearchControls={false}
                      rowClassName={() => "summary-row"}
                      middleColumnKey={["total_day_variance", "total_night_variance"]}
                    />
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </>
    ) : (
      <p>No shift report data found for selected dates.</p>
    )}
  </>
)}
      </div>
    </Layout>
  );
};

export default ShiftReportComponent;
